import { Check } from "lucide-react";
import { useLang } from "../lib/i18n";
import { useAppData } from "../context/AppDataContext";

const UNIT_BY_OP = { alquiler: "mes", vacacional: "noche", venta: "total" };

export function Label({ children, error }) {
  return (
    <div className="mb-1.5">
      <span className="text-sm font-bold text-navy">{children}</span>
      {error && <p className="mt-0.5 text-xs font-semibold text-alert" data-testid="field-error">{error}</p>}
    </div>
  );
}

function Chips({ field, options, value, onChange, multi }) {
  const { optLabel } = useAppData();
  const isOn = (o) => (multi ? (value || []).includes(o) : value === o);
  const toggle = (o) => {
    if (!multi) return onChange(value === o ? "" : o);
    const cur = value || [];
    onChange(cur.includes(o) ? cur.filter((x) => x !== o) : [...cur, o]);
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid={`attr-${field}`}>
      {options.map((o) => (
        <button type="button" key={o} onClick={() => toggle(o)} data-testid={`attr-${field}-${o}`}
          className={`rounded-full border-[1.5px] px-3.5 py-1.5 text-sm font-semibold transition-colors duration-200 ${
            isOn(o) ? "border-brand bg-brand text-white" : "border-slate-200 text-navy hover:border-brand/60"}`}>
          {optLabel(field, o)}
        </button>
      ))}
    </div>
  );
}

function Checks({ field, options, value, onChange }) {
  const { optLabel } = useAppData();
  const cur = value || [];
  return (
    <div className="grid grid-cols-2 gap-2" data-testid={`attr-${field}`}>
      {options.map((o) => {
        const on = cur.includes(o);
        return (
          <button type="button" key={o} onClick={() => onChange(on ? cur.filter((x) => x !== o) : [...cur, o])} data-testid={`attr-${field}-${o}`}
            className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-left text-sm font-medium transition-colors duration-200 ${on ? "border-brand bg-brand/10 text-navy" : "border-slate-200 text-navy"}`}>
            <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${on ? "border-brand bg-brand text-white" : "border-slate-300"}`}>
              {on && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
            </span>
            {optLabel(field, o)}
          </button>
        );
      })}
    </div>
  );
}

function Num({ field, value, onChange, placeholder }) {
  return (
    <input className="field" type="number" inputMode="numeric" min="0" value={value ?? ""} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} data-testid={`attr-${field}-input`} />
  );
}

export default function AttributeFields({ kind, attrs, onChange, errors = {} }) {
  const { t } = useLang();
  const { schemas } = useAppData();
  const schema = schemas[kind] || {};
  const set = (k) => (v) => onChange({ ...attrs, [k]: v });
  const opts = (k) => schema[k]?.options || [];

  if (kind === "contactos")
    return (
      <div className="space-y-5">
        <div className="grid grid-cols-[1fr_110px] gap-3">
          <div>
            <Label error={errors.name}>{t("form.name")}</Label>
            <input className="field" maxLength={40} value={attrs.name || ""} onChange={(e) => set("name")(e.target.value)} data-testid="attr-name-input" />
          </div>
          <div>
            <Label error={errors.age}>{t("form.age")}</Label>
            <Num field="age" value={attrs.age} onChange={set("age")} placeholder="18+" />
          </div>
        </div>
        <div><Label>{t("form.gender")}</Label><Chips field="gender" options={opts("gender")} value={attrs.gender} onChange={set("gender")} /></div>
        <div><Label error={errors.looking_for}>{t("form.lookingFor")}</Label><Chips field="looking_for" multi options={opts("looking_for")} value={attrs.looking_for} onChange={set("looking_for")} /></div>
        <div><Label>{t("form.languages")}</Label><Chips field="languages" multi options={opts("languages")} value={attrs.languages} onChange={set("languages")} /></div>
      </div>
    );

  if (kind === "property")
    return (
      <div className="space-y-5">
        <div>
          <Label error={errors.operation}>{t("form.operation")}</Label>
          <Chips field="operation" options={opts("operation")} value={attrs.operation}
            onChange={(v) => onChange({ ...attrs, operation: v, price_unit: UNIT_BY_OP[v] || attrs.price_unit })} />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><Label>{t("form.rooms")}</Label><Num field="rooms" value={attrs.rooms} onChange={set("rooms")} /></div>
          <div><Label>{t("form.baths")}</Label><Num field="bathrooms" value={attrs.bathrooms} onChange={set("bathrooms")} /></div>
          <div><Label>{t("form.area")}</Label><Num field="area" value={attrs.area} onChange={set("area")} /></div>
        </div>
        <div><Label>{t("form.features")}</Label><Checks field="features" options={opts("features")} value={attrs.features} onChange={set("features")} /></div>
      </div>
    );

  if (kind === "job")
    return (
      <div className="space-y-5">
        <div><Label>{t("form.contract")}</Label><Chips field="contract" options={opts("contract")} value={attrs.contract} onChange={set("contract")} /></div>
        <div><Label>{t("form.schedule")}</Label><Chips field="schedule" options={opts("schedule")} value={attrs.schedule} onChange={set("schedule")} /></div>
        <div>
          <Label>{t("form.salary")}</Label>
          <input className="field" maxLength={60} value={attrs.salary || ""} placeholder={t("form.salaryPh")} onChange={(e) => set("salary")(e.target.value)} data-testid="attr-salary-input" />
        </div>
      </div>
    );

  if (kind === "motor")
    return (
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-3">
          <div><Label>{t("form.year")}</Label><Num field="year" value={attrs.year} onChange={set("year")} /></div>
          <div><Label>{t("form.km")}</Label><Num field="km" value={attrs.km} onChange={set("km")} /></div>
        </div>
        <div><Label>{t("form.fuel")}</Label><Chips field="fuel" options={opts("fuel")} value={attrs.fuel} onChange={set("fuel")} /></div>
      </div>
    );

  return null;
}

export function PriceUnitChips({ value, onChange }) {
  const { schemas } = useAppData();
  return <Chips field="price_unit" options={schemas.property?.price_unit?.options || []} value={value} onChange={onChange} />;
}

export function ConsentCheck({ checked, onChange, error, label = "form.consent", testid = "publish-consent-checkbox" }) {
  const { t } = useLang();
  return (
    <div>
      <button type="button" onClick={() => onChange(!checked)} data-testid={testid}
        className={`flex w-full items-start gap-3 rounded-2xl border p-3.5 text-left text-sm transition-colors duration-200 ${checked ? "border-brand bg-brand/5" : error ? "border-alert" : "border-slate-200"}`}>
        <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${checked ? "border-brand bg-brand text-white" : "border-slate-400"}`}>
          {checked && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
        </span>
        <span className="text-navy">{t(label)}</span>
      </button>
      {error && <p className="mt-1 text-xs font-semibold text-alert" data-testid="field-error">{error}</p>}
    </div>
  );
}
